import React, { Component } from "react";
import {
  message,
  PageHeader,
  Spin,
  Row,
  Col,
  Select,
  Steps,
  Table,
  Tag,
  Tooltip,
  Progress,
  Result,
  Alert,
  Input,
  DatePicker,
  Switch,
} from "antd";
import { CheckCircleOutlined, WarningOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";
import { connect } from "react-redux";
import {
  ButtonItem,
  SelectItem,
  SelectInsumosItem,
  dineroDisabledMask,
  round2Decimals,
} from "../../../../Components/Items";
import {
  extraerDatosDesdeArbol,
  resolverProveedorPorRfc,
  inferirPersonaPorRfc,
  textoTraslados,
} from "../../ArchivosXml/xmlConceptosHelpers";
import {
  leerImpuestosCfdi,
  resolverImpuestosXml,
  mensajeFaltantes,
} from "../../../../Utils/impuestosXmlCompra";

const { Option } = Select;
const { Step } = Steps;
const { RangePicker } = DatePicker;

moment.locale("es");

const MONEDA_MXN_ID = "60c7e4396465ad440c41c15a";

// TipoRelacion 04 es la sustitucion de un CFDI previo (otra nota), no una factura a la
// que se le aplique la nota.
const TIPO_RELACION_SUSTITUCION = "04";

// Cuantas notas resuelven sus facturas relacionadas a la vez, para no mandar todo el lote
// de golpe al servidor (son dos consultas por cada UUID relacionado).
const NOTAS_POR_TANDA = 5;

// Normaliza texto para comparar sin distinguir mayúsculas ni acentos.
const normalizarTexto = (str) =>
  String(str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

/**
 * UUIDs de los nodos cfdi:CfdiRelacionados del comprobante. En CFDI 4.0 puede venir un
 * nodo por cada TipoRelacion, por eso se recorren todos y no solo el primero.
 */
const leerCfdiRelacionados = (raiz) =>
  (raiz?.children || [])
    .filter((nodo) => nodo.name === "cfdi:CfdiRelacionados")
    .flatMap((nodo) =>
      (nodo.children || [])
        .filter((hijo) => hijo.name === "cfdi:CfdiRelacionado" && hijo.attributes?.UUID)
        .map((hijo) => ({
          uuid: hijo.attributes.UUID,
          tipoRelacion: nodo.attributes?.TipoRelacion,
        }))
    );

/**
 * Importar Notas de Crédito XML (compras).
 *
 * Es el equivalente de Importar Gastos XML para los CFDI de egreso de los proveedores: se
 * eligen varios XML descargados y se registran sin relacionar sus conceptos con insumos.
 * Los impuestos y retenciones se toman tal cual del XML y se guardan en la nota
 * (`impuestosDesdeXml`), asi que la poliza y la DIOT usan exactamente esos importes.
 *
 * El insumo de cada nota solo define la familia (si el IVA de la nota va a gasto no
 * deducible): el subtotal siempre se abona a Descuentos sobre compras.
 *
 * Si el XML relaciona facturas ya registradas del proveedor y su saldo alcanza, la nota se
 * aplica a ellas al guardarse (`pagarFactura`, igual que en Agregar Nota de Crédito). Si
 * no, queda como saldo a favor del proveedor para aplicarla desde Cuentas por Pagar.
 */
class ImportarNotasDeCreditoXml extends Component {
  state = {
    loading: false,
    paso: 0,

    empresaRfc: null,
    todasProveedores: [],
    objetoProyectos: null,
    todasMonedas: [],
    todasTiposDeProveedor: [],
    todasImpuestos: [],
    todasRetenciones: [],
    // La empresa no tiene un IVA marcado como Exento en el catalogo: todo CFDI que traiga
    // conceptos exentos se va a rechazar, asi que mas vale decirlo antes de cargar el lote.
    faltaImpuestoExento: false,

    cargandoLista: false,
    xmlsDisponibles: [],
    seleccionadosKeys: [],
    filtroTextoXml: "",
    filtroRangoFechasXml: null,

    archivos: [],
    tipoProveedorId: undefined,

    proyectoId: undefined,

    filas: [],
    seleccionFilasKeys: [],
    insumoMasivoTemp: null,
    filtroFilas: "",

    guardando: false,
    terminado: false,
    progreso: { actual: 0, total: 0 },
    resultados: [],
  };

  componentDidMount() {
    this.setState({ loading: true });
    Promise.all([this.consultarDatos(), this.consultarXmlsDisponibles()]).finally(() =>
      this.setState({ loading: false })
    );
  }

  async consultarDatos() {
    const [resDatos, resTipos] = await Promise.all([
      axios.post(
        "facturasSinOrdenDeCompra/selectDatosParaFacturaSinOrdenDeCompra",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      ),
      axios.post(
        "tiposdeproveedores/selectTiposDeProveedores",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      ),
    ]);

    const newState = {};

    if (resDatos.data.configuracion) {
      newState.empresaRfc = resDatos.data.configuracion.rfc;
    } else {
      message.error("No se encontró la configuración de esta empresa");
    }

    newState.todasProveedores = resDatos.data.proveedores || [];
    newState.objetoProyectos = (resDatos.data.proyectos || []).map((v) => (
      <Option value={v._id} key={v._id}>{v.nombre}</Option>
    ));
    newState.todasMonedas = resDatos.data.monedas || [];
    newState.todasImpuestos = resDatos.data.impuestos || [];
    newState.todasRetenciones = resDatos.data.retenciones || [];

    // tasaCuota 5 es el unico marcador de exento del catalogo
    newState.faltaImpuestoExento = !newState.todasImpuestos.some(
      (valueImpuesto) => Number(valueImpuesto.impuesto) === 2 && Number(valueImpuesto.tasaCuota) === 5
    );

    if (Array.isArray(resTipos.data)) {
      newState.todasTiposDeProveedor = resTipos.data;
    }

    this.setState(newState);
  }

  // ---------- Paso 1: elegir XMLs ----------

  async consultarXmlsDisponibles() {
    this.setState({ cargandoLista: true });
    try {
      const res = await axios.post(
        "descargaMasiva/selectTodosXml",
        { empresaId: this.props.empresaId, valor: 1, filtroTipo: "E", page: 1, pageSize: 500 },
        { headers: this.props.headersToken }
      );
      if (res.data.success === 2) {
        const disponibles = (res.data.data || []).filter(
          (r) => (r.estado === 1 || r.estado === 6) && r.estadoCancelacion !== 1
        );
        this.setState({ xmlsDisponibles: disponibles });
      } else {
        this.setState({ xmlsDisponibles: [] });
      }
    } finally {
      this.setState({ cargandoLista: false });
    }
  }

  /**
   * Lee un XML ya descargado y saca de el todo lo que necesita la nota: los datos del
   * comprobante, las facturas que relaciona y el desglose de impuestos/retenciones ya
   * identificado contra el catalogo.
   */
  async obtenerArchivoDesdeXmlDescargado(row) {
    const nombre = `${row.serie || ""} ${row.folio || ""}`.trim() || row.emisorNombre || row.uuid;
    try {
      const resDatos = await axios.post(
        "descargaMasiva/datosXml",
        { xmlId: row._id, empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      );

      if (resDatos.data.success !== 2) {
        return { uid: row._id, nombre, estado: "error", motivo: "No se pudo leer el XML" };
      }

      const datos = extraerDatosDesdeArbol(resDatos.data.dataXml);
      if (!datos.uuid) datos.uuid = row.uuid;

      if (datos.tipoDeComprobante !== "E") {
        return { uid: row._id, nombre, estado: "error", motivo: "El XML no es una nota de crédito (egreso)" };
      }
      if ((datos.rfcReceptor || "").toUpperCase() !== (this.state.empresaRfc || "").toUpperCase()) {
        return { uid: row._id, nombre, estado: "error", motivo: "El RFC del receptor no coincide con el de la empresa" };
      }

      // Impuestos y retenciones del CFDI, identificados contra el catalogo de la empresa
      const {
        traslados,
        retenciones,
        avisos: avisosLectura,
      } = leerImpuestosCfdi(resDatos.data.dataXml);
      // `avisosCatalogo` trae las tasas que venian mal escritas y se tuvieron que interpretar
      const {
        arrayImpuestos,
        arrayRetenciones,
        faltantes,
        avisos: avisosCatalogo,
      } = resolverImpuestosXml(
        this.state.todasImpuestos,
        this.state.todasRetenciones,
        traslados,
        retenciones
      );
      const avisos = [...avisosLectura, ...avisosCatalogo];

      const mensaje = mensajeFaltantes(faltantes);
      if (mensaje) {
        return { uid: row._id, nombre, estado: "error", motivo: mensaje };
      }

      // Revisa facturas y notas de credito de compra
      let estado = "valido";
      try {
        const resUuid = await axios.post(
          "facturas/checarUUID",
          { empresaId: this.props.empresaId, uuid: datos.uuid },
          { headers: this.props.headersToken }
        );
        if (resUuid.data.existeUUID) estado = "duplicado";
      } catch (e) {
        // si falla la validacion de duplicado se deja pasar; el backend la vuelve a hacer
      }

      return {
        uid: row._id,
        nombre,
        estado,
        datos,
        relacionados: leerCfdiRelacionados(resDatos.data.dataXml),
        arrayImpuestos,
        arrayRetenciones,
        avisos,
      };
    } catch (e) {
      return { uid: row._id, nombre, estado: "error", motivo: "Error al leer el XML" };
    }
  }

  async avanzarAPasoProveedor() {
    const { xmlsDisponibles, seleccionadosKeys } = this.state;
    const filas = xmlsDisponibles.filter((r) => seleccionadosKeys.includes(r._id));

    this.setState({ loading: true });
    const archivos = await Promise.all(filas.map((r) => this.obtenerArchivoDesdeXmlDescargado(r)));
    this.setState({ archivos, loading: false, paso: 1 });
  }

  xmlsDisponiblesFiltrados() {
    const { xmlsDisponibles, filtroTextoXml, filtroRangoFechasXml } = this.state;

    let resultado = xmlsDisponibles;

    const texto = (filtroTextoXml || "").trim().toLowerCase();
    if (texto) {
      resultado = resultado.filter((r) => {
        const rfc = r.emisor?._attributes?.Rfc || "";
        const campos = [r.serie, r.folio, r.emisorNombre, rfc, r.uuid];
        return campos.some((c) => (c || "").toLowerCase().includes(texto));
      });
    }

    const [fechaDesde, fechaHasta] = filtroRangoFechasXml || [];
    if (fechaDesde) resultado = resultado.filter((r) => r.fecha && moment(r.fecha).isSameOrAfter(fechaDesde, "day"));
    if (fechaHasta) resultado = resultado.filter((r) => r.fecha && moment(r.fecha).isSameOrBefore(fechaHasta, "day"));

    return resultado;
  }

  renderPasoXmls() {
    const {
      xmlsDisponibles,
      seleccionadosKeys,
      cargandoLista,
      filtroTextoXml,
      filtroRangoFechasXml,
    } = this.state;

    const xmlsFiltrados = this.xmlsDisponiblesFiltrados();
    const hayFiltrosActivos = !!filtroTextoXml || !!filtroRangoFechasXml;

    return (
      <>
        <Row justify="space-between" style={{ marginBottom: 8 }}>
          <Col><p>Elige los XML de notas de crédito que quieres registrar:</p></Col>
          <Col>
            <ButtonItem onClick={() => this.consultarXmlsDisponibles()} loading={cargandoLista}>
              Actualizar lista
            </ButtonItem>
          </Col>
        </Row>

        <Row gutter={[8, 8]} style={{ marginBottom: 8 }} align="middle">
          <Col xs={24} sm={12} lg={8}>
            <Input.Search
              allowClear
              placeholder="Buscar por serie, folio, emisor, RFC o UUID"
              value={filtroTextoXml}
              onChange={(e) => this.setState({ filtroTextoXml: e.target.value })}
            />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <RangePicker
              allowClear
              style={{ width: "100%" }}
              placeholder={["Fecha desde", "Fecha hasta"]}
              value={filtroRangoFechasXml}
              onChange={(v) => this.setState({ filtroRangoFechasXml: v })}
            />
          </Col>
        </Row>

        {hayFiltrosActivos && (
          <Row justify="space-between" style={{ marginBottom: 8 }}>
            <Col><Tag color="blue">{xmlsFiltrados.length} de {xmlsDisponibles.length} XML(s) con los filtros aplicados</Tag></Col>
            <Col>
              <ButtonItem
                size="small"
                onClick={() => this.setState({ filtroTextoXml: "", filtroRangoFechasXml: null })}
              >
                Limpiar filtros
              </ButtonItem>
            </Col>
          </Row>
        )}

        <Table
          size="small"
          rowKey="_id"
          loading={cargandoLista}
          dataSource={xmlsFiltrados}
          pagination={{ defaultPageSize: 10 }}
          rowSelection={{
            selectedRowKeys: seleccionadosKeys,
            onChange: (keys) => this.setState({ seleccionadosKeys: keys }),
          }}
          columns={[
            {
              title: "Serie/Folio",
              render: (_, r) => `${r.serie || ""} ${r.folio || ""}`.trim() || "-",
              sorter: (a, b) => `${a.serie || ""}${a.folio || ""}`.localeCompare(`${b.serie || ""}${b.folio || ""}`),
            },
            {
              title: "Emisor",
              dataIndex: "emisorNombre",
              sorter: (a, b) => (a.emisorNombre || "").localeCompare(b.emisorNombre || ""),
            },
            { title: "RFC", render: (_, r) => r.emisor?._attributes?.Rfc || "-" },
            { title: "UUID", dataIndex: "uuid" },
            {
              title: "Fecha",
              render: (_, r) => (r.fecha ? moment(r.fecha).format("L") : "-"),
              sorter: (a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0),
              defaultSortOrder: "descend",
            },
            {
              title: "Total",
              render: (_, r) => (typeof r.total === "number" ? r.total.toFixed(2) : "-"),
              align: "right",
              sorter: (a, b) => (a.total || 0) - (b.total || 0),
            },
          ]}
        />

        <Row justify="end" style={{ marginTop: 20 }}>
          <ButtonItem
            type="primary"
            loading={this.state.loading}
            disabled={seleccionadosKeys.length === 0}
            onClick={() => this.avanzarAPasoProveedor()}
          >
            Siguiente ({seleccionadosKeys.length})
          </ButtonItem>
        </Row>
      </>
    );
  }

  // ---------- Paso 2: proveedores que faltan por dar de alta ----------

  calcularRfcsNuevos() {
    const { archivos, todasProveedores } = this.state;
    const vistos = new Set();
    const resultado = [];
    archivos
      .filter((a) => a.estado === "valido" || a.estado === "duplicado")
      .forEach((a) => {
        const rfc = a.datos.rfcEmisor;
        if (!rfc || vistos.has(rfc.toUpperCase())) return;
        vistos.add(rfc.toUpperCase());
        if (!resolverProveedorPorRfc(todasProveedores, rfc)) {
          resultado.push({ rfc, nombre: a.datos.nombreEmisor });
        }
      });
    return resultado;
  }

  async avanzarAPasoProyecto() {
    const rfcsNuevos = this.calcularRfcsNuevos();

    if (rfcsNuevos.length === 0) {
      this.setState({ paso: 2 });
      return;
    }

    if (!this.state.tipoProveedorId) {
      message.error("Selecciona un tipo de proveedor para dar de alta los que faltan");
      return;
    }

    this.setState({ loading: true });
    const proveedoresActualizados = [...this.state.todasProveedores];

    for (const { rfc, nombre } of rfcsNuevos) {
      const persona = inferirPersonaPorRfc(rfc);
      const body = {
        empresaId: this.props.empresaId,
        tipoProveedorId: this.state.tipoProveedorId,
        rfc,
        persona,
        nombre: !persona ? nombre : "",
        razonSocial: persona ? nombre : "",
        monedaId: MONEDA_MXN_ID,
        tipoOperacion: 3,
        tipoTercero: 1,
        pagoElectronico: false,
        estado: 1,
      };

      try {
        const res = await axios.post("proveedores/agregarProveedor", body, { headers: this.props.headersToken });
        if (res.data.success === 2) {
          proveedoresActualizados.push({ ...body, _id: res.data.codigo, diasCredito: 0, retenciones: [] });
        } else {
          message.warning(`No se pudo dar de alta el proveedor con RFC ${rfc}: ${res.data.message || "error desconocido"}`);
        }
      } catch (e) {
        message.warning(`Error al dar de alta el proveedor con RFC ${rfc}`);
      }
    }

    this.setState({ todasProveedores: proveedoresActualizados, loading: false, paso: 2 });
  }

  renderPasoProveedor() {
    const rfcsNuevos = this.calcularRfcsNuevos();
    const errores = this.state.archivos.filter((a) => a.estado === "error");
    const duplicados = this.state.archivos.filter((a) => a.estado === "duplicado");

    return (
      <>
        {errores.length > 0 && (
          <Alert
            style={{ marginBottom: 16 }}
            type="warning"
            showIcon
            message={`${errores.length} XML no se pudieron incluir`}
            description={errores.map((e) => `${e.nombre}: ${e.motivo}`).join(" · ")}
          />
        )}
        {duplicados.length > 0 && (
          <Alert
            style={{ marginBottom: 16 }}
            type="info"
            showIcon
            message={`${duplicados.length} XML ya están registrados con ese UUID`}
            description="Quedarán marcados como duplicados y no se pueden guardar."
          />
        )}

        {rfcsNuevos.length === 0 ? (
          <Alert
            type="success"
            showIcon
            message="Todos los proveedores de estos XML ya están registrados"
          />
        ) : (
          <>
            <Row>
              <Col xs={24} sm={12}>
                <p>Tipo de proveedor con el que se darán de alta los proveedores nuevos:</p>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Tipo de proveedor"
                  value={this.state.tipoProveedorId}
                  onChange={(v) => this.setState({ tipoProveedorId: v })}
                >
                  {this.state.todasTiposDeProveedor.map((t) => (
                    <Option value={t._id} key={t._id}>{t.nombre}</Option>
                  ))}
                </Select>
              </Col>
            </Row>
            <Alert
              style={{ marginTop: 16 }}
              type="info"
              showIcon
              message={`Se darán de alta ${rfcsNuevos.length} proveedor(es) nuevo(s)`}
              description={rfcsNuevos.map((r) => `${r.nombre} (${r.rfc})`).join(", ")}
            />
          </>
        )}

        <Row justify="space-between" style={{ marginTop: 20 }}>
          <ButtonItem onClick={() => this.setState({ paso: 0 })}>Atrás</ButtonItem>
          <ButtonItem type="primary" loading={this.state.loading} onClick={() => this.avanzarAPasoProyecto()}>
            Siguiente
          </ButtonItem>
        </Row>
      </>
    );
  }

  // ---------- Paso 3: proyecto ----------

  async avanzarAPasoNotas() {
    this.setState({ loading: true });
    await this.construirFilas();
    this.setState({ loading: false, paso: 3 });
  }

  renderPasoProyecto() {
    return (
      <>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <p>Proyecto en el que se registrarán todas las notas de este lote:</p>
            <SelectItem
              placeholder="Proyecto"
              style={{ width: "100%" }}
              value={this.state.proyectoId}
              onChange={(value) => this.setState({ proyectoId: value })}
            >
              {this.state.objetoProyectos}
            </SelectItem>
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginTop: 20 }}>
          <ButtonItem onClick={() => this.setState({ paso: 1 })}>Atrás</ButtonItem>
          <ButtonItem
            type="primary"
            loading={this.state.loading}
            disabled={!this.state.proyectoId}
            onClick={() => this.avanzarAPasoNotas()}
          >
            Siguiente
          </ButtonItem>
        </Row>
      </>
    );
  }

  // ---------- Paso 4: revisar cada nota ----------

  /**
   * Busca cada UUID relacionado del XML entre las facturas del proveedor y trae su saldo.
   * `checarUUIDRelacionados` ya revisa que la factura sea de ese proveedor, pero no trae lo
   * pagado, por eso se pide tambien la factura.
   */
  async resolverFacturasRelacionadas(relacionados, proveedorId) {
    const resultado = [];

    for (const { uuid, tipoRelacion } of relacionados || []) {
      const base = { uuid, tipoRelacion };

      if (tipoRelacion === TIPO_RELACION_SUSTITUCION) {
        resultado.push({ ...base, encontrada: false, sustitucion: true, motivo: "Sustituye a otro CFDI; no es una factura" });
        continue;
      }

      try {
        const resUuid = await axios.post(
          "facturas/checarUUIDRelacionados",
          { empresaId: this.props.empresaId, uuid, proveedorId },
          { headers: this.props.headersToken }
        );
        if (!resUuid.data.existeUUID) {
          // Solo trae mensaje cuando la factura existe pero es de otro proveedor
          const motivo = resUuid.data.message ? "La factura es de otro proveedor" : "La factura no está registrada";
          resultado.push({ ...base, encontrada: false, motivo });
          continue;
        }

        const resFactura = await axios.post(
          "notasDeCreditoCompra/datosFactura",
          { empresaId: this.props.empresaId, facturaId: resUuid.data.facturaId },
          { headers: this.props.headersToken }
        );
        const factura = resFactura.data;
        if (!factura?._id) {
          resultado.push({ ...base, encontrada: false, motivo: "No se pudo consultar la factura" });
          continue;
        }

        resultado.push({
          ...base,
          encontrada: true,
          facturaId: factura._id,
          codigoFactura: factura.codigoFactura,
          monedaId: factura.monedaId,
          monedaAbrev: factura.moneda?.abreviacion,
          tipoDeCambio: factura.tipoDeCambio || 1,
          totalPagar: factura.totalPagar,
          saldo: round2Decimals((factura.totalPagar || 0) - (factura.totalPagado || 0)),
        });
      } catch (e) {
        resultado.push({ ...base, encontrada: false, motivo: "Error al consultar la factura" });
      }
    }

    return resultado;
  }

  /**
   * Arma una fila por XML con el proveedor resuelto, sus facturas relacionadas y el gasto
   * predeterminado del proveedor como insumo. Los importes son los del XML, no se calculan.
   */
  async construirFilas() {
    const { archivos, todasProveedores } = this.state;

    const armarFila = async (archivo) => {
      // `bloqueada` = el problema no se puede arreglar desde esta pantalla, asi que la fila
      // ni siquiera deja elegir insumo.
      if (archivo.estado === "error") {
        return { ...archivo, filaEstado: "error", motivoFila: archivo.motivo, incluir: false, bloqueada: true };
      }

      const proveedor = resolverProveedorPorRfc(todasProveedores, archivo.datos.rfcEmisor);
      if (!proveedor) {
        return {
          ...archivo,
          filaEstado: "error",
          motivoFila: `No se pudo resolver el proveedor con RFC ${archivo.datos.rfcEmisor}`,
          incluir: false,
          bloqueada: true,
        };
      }

      const relacionadas = await this.resolverFacturasRelacionadas(archivo.relacionados, proveedor._id);

      return this.recalcularFila({
        ...archivo,
        proveedor,
        relacionadas,
        aplicarFactura: true,
        insumoId: proveedor.insumoGastoId,
        insumoCompleto: null,
      });
    };

    const filas = [];
    for (let i = 0; i < archivos.length; i += NOTAS_POR_TANDA) {
      filas.push(...(await Promise.all(archivos.slice(i, i + NOTAS_POR_TANDA).map(armarFila))));
    }

    // Se traen de golpe los insumos predeterminados para poder mostrar su nombre
    const idsInsumo = Array.from(
      new Set(filas.map((f) => f.insumoId).filter(Boolean).map((id) => id.toString()))
    );

    if (idsInsumo.length > 0) {
      try {
        const res = await axios.post(
          "insumos/selectInsumosPorIds",
          { empresaId: this.props.empresaId, insumoIds: idsInsumo },
          { headers: this.props.headersToken }
        );
        const insumos = res.data?.insumos || [];
        if (Array.isArray(insumos) && insumos.length > 0) {
          const porId = new Map(insumos.map((i) => [i._id.toString(), i]));
          const filasConInsumo = filas.map((f) =>
            f.insumoId && porId.has(f.insumoId.toString())
              ? this.recalcularFila({ ...f, insumoCompleto: porId.get(f.insumoId.toString()) })
              : f
          );
          this.setState({ filas: filasConInsumo });
          return;
        }
      } catch (e) {
        // Si no se pudieron precargar, el select los resuelve solo al renderizar
      }
    }

    this.setState({ filas });
  }

  /**
   * Decide si la nota se puede aplicar a sus facturas relacionadas y cuanto le toca a cada
   * una. Solo se aplica si todas se encontraron, estan en pesos igual que la nota y entre
   * todas alcanzan el total: el backend exige que lo aplicado sea igual al total a pagar de
   * la nota. El reparto va en el orden del XML, hasta el saldo de cada factura.
   */
  calcularAplicacion(relacionadas, monedaId, totalPagar) {
    const candidatas = (relacionadas || []).filter((r) => !r.sustitucion);

    if (candidatas.length === 0) {
      return { puedeAplicar: false, motivo: "El XML no relaciona ninguna factura", montos: [] };
    }

    const noEncontrada = candidatas.find((r) => !r.encontrada);
    if (noEncontrada) {
      return { puedeAplicar: false, motivo: `${noEncontrada.uuid}: ${noEncontrada.motivo}`, montos: [] };
    }

    if (
      String(monedaId) !== MONEDA_MXN_ID ||
      candidatas.some((r) => String(r.monedaId) !== MONEDA_MXN_ID)
    ) {
      return {
        puedeAplicar: false,
        motivo: "La nota o la factura no está en pesos; aplícala desde Cuentas por Pagar",
        montos: [],
      };
    }

    const saldoTotal = round2Decimals(candidatas.reduce((acumulado, r) => acumulado + Math.max(0, r.saldo), 0));
    if (saldoTotal < totalPagar) {
      return {
        puedeAplicar: false,
        motivo: `El saldo pendiente de las facturas (${saldoTotal}) no alcanza el total de la nota (${totalPagar})`,
        montos: [],
      };
    }

    let restante = totalPagar;
    const montos = [];
    for (const r of candidatas) {
      const monto = round2Decimals(Math.min(Math.max(0, r.saldo), restante));
      restante = round2Decimals(restante - monto);
      // El backend rechaza un montoPago de 0 cuando se aplica la nota
      if (monto > 0) {
        montos.push({ facturaId: r.facturaId, tipoDeCambioCompra: r.tipoDeCambio, montoPago: monto });
      }
    }

    return { puedeAplicar: true, montos };
  }

  /**
   * Recalcula estado y payload de una fila. Los importes salen del XML: aqui solo se
   * verifica que cuadren y se arma el renglon con el insumo elegido.
   */
  recalcularFila(fila) {
    const { proyectoId, todasMonedas } = this.state;
    const d = fila.datos;
    const proveedor = fila.proveedor;

    const subtotal = round2Decimals(d.subTotalXml);
    const totalImpuestos = round2Decimals(
      (fila.arrayImpuestos || []).reduce((acumulado, v) => acumulado + v.totalImpuestos, 0)
    );
    const totalRetenciones = round2Decimals(
      (fila.arrayRetenciones || []).reduce((acumulado, v) => acumulado + v.totalRetenciones, 0)
    );
    const total = round2Decimals(subtotal + totalImpuestos);
    const totalPagar = round2Decimals(total - totalRetenciones);

    const moneda = todasMonedas.find((m) => (m.abreviacion || "").toLowerCase() === (d.monedaAbrev || "").toLowerCase());
    const monedaId = moneda ? moneda._id : MONEDA_MXN_ID;

    const aplicacion = this.calcularAplicacion(fila.relacionadas, monedaId, totalPagar);
    const pagarFactura = aplicacion.puedeAplicar && fila.aplicarFactura !== false;

    const base = { ...fila, subtotal, totalImpuestos, totalRetenciones, total, totalPagar, aplicacion, pagarFactura };

    // El comprobante tiene que cuadrar contra lo que se va a contabilizar
    if (round2Decimals(d.totalXml - totalPagar) !== 0) {
      return {
        ...base,
        filaEstado: "error",
        motivoFila: `El total del XML (${d.totalXml}) no cuadra con subtotal + impuestos - retenciones (${totalPagar})`,
        incluir: false,
        bloqueada: true,
        payload: undefined,
      };
    }

    if (!fila.insumoId) {
      return {
        ...base,
        filaEstado: "faltaAsignar",
        motivoFila: "Elige el insumo de esta nota",
        incluir: false,
        payload: undefined,
      };
    }

    const filaEstado = fila.estado === "duplicado" ? "duplicado" : "nuevo";
    const motivoFila = fila.estado === "duplicado" ? "El UUID ya está registrado" : undefined;

    // Las facturas se guardan como referencia aunque la nota no se aplique, igual que en
    // Agregar Nota de Crédito; el montoPago solo va cuando si se aplica.
    const facturasRelacionadas = pagarFactura
      ? aplicacion.montos
      : (fila.relacionadas || [])
          .filter((r) => r.encontrada)
          .map((r) => ({ facturaId: r.facturaId, tipoDeCambioCompra: r.tipoDeCambio }));

    const payload = {
      empresaId: this.props.empresaId,
      proveedorId: proveedor._id,
      proyectoId,
      //Marca la nota: la poliza y la DIOT usan estos importes, no los del catalogo del insumo
      impuestosDesdeXml: true,
      insumos: [
        {
          insumoId: fila.insumoId,
          cantidad: 1,
          precioUnitario: subtotal,
          descuento: 0,
          subtotal,
          impuestos: totalImpuestos,
          total,
        },
      ],
      arrayImpuestos: fila.arrayImpuestos,
      arrayRetenciones: fila.arrayRetenciones,
      total,
      subtotal,
      cuentasProveedor: totalRetenciones,
      totalPagar,
      ajusteSubtotal: 0,
      fechaFactura: moment(d.fechaFactura),
      monedaId,
      tipoDeCambio: d.tipoDeCambio,
      uuid: d.uuid,
      uuidRelacionado: (fila.relacionadas || []).find((r) => !r.sustitucion)?.uuid,
      codigoFactura: d.codigoFactura,
      descripcion: (d.conceptos || []).map((c) => c.descripcion).filter(Boolean).join(" | ").substring(0, 500),
      pagarFactura,
      facturasRelacionadas,
    };

    return { ...base, filaEstado, motivoFila, incluir: filaEstado === "nuevo", payload };
  }

  cambiarInsumoFila(uid, insumoId, insumoCompleto) {
    this.setState((prev) => ({
      filas: prev.filas.map((f) =>
        f.uid === uid && !f.bloqueada
          ? this.recalcularFila({ ...f, insumoId, insumoCompleto })
          : f
      ),
    }));
  }

  cambiarAplicarFila(uid, aplicarFactura) {
    this.setState((prev) => ({
      filas: prev.filas.map((f) =>
        f.uid === uid && !f.bloqueada ? this.recalcularFila({ ...f, aplicarFactura }) : f
      ),
    }));
  }

  /** Pone el mismo insumo en todas las notas seleccionadas de la tabla. */
  asignarInsumoASeleccionadas() {
    const { insumoMasivoTemp, seleccionFilasKeys } = this.state;
    if (!insumoMasivoTemp) return;

    this.setState((prev) => ({
      filas: prev.filas.map((f) =>
        seleccionFilasKeys.includes(f.uid) && !f.bloqueada
          ? this.recalcularFila({ ...f, insumoId: insumoMasivoTemp._id, insumoCompleto: insumoMasivoTemp })
          : f
      ),
      seleccionFilasKeys: [],
    }));
  }

  marcarFila(uid, incluir) {
    this.setState((prev) => ({
      filas: prev.filas.map((f) => (f.uid === uid ? { ...f, incluir } : f)),
    }));
  }

  async guardarTodas() {
    const filasAGuardar = this.state.filas.filter((f) => f.incluir && f.payload);
    this.setState({ guardando: true, progreso: { actual: 0, total: filasAGuardar.length } });

    const resultados = [];

    // Una por una: si dos notas se aplican a la misma factura, la segunda tiene que ver el
    // saldo que dejo la primera (el backend lo vuelve a revisar).
    for (let i = 0; i < filasAGuardar.length; i++) {
      const fila = filasAGuardar[i];
      try {
        const res = await axios.post("notasDeCreditoCompra/agregarNotaDeCredito", fila.payload, {
          headers: this.props.headersToken,
        });
        if (res.data.success === 2) {
          resultados.push({ uid: fila.uid, nombre: fila.nombre, ok: true });
        } else {
          resultados.push({ uid: fila.uid, nombre: fila.nombre, ok: false, motivo: res.data.message || "Error al guardar" });
        }
      } catch (e) {
        resultados.push({ uid: fila.uid, nombre: fila.nombre, ok: false, motivo: "Error de comunicación con el servidor" });
      }
      this.setState({ progreso: { actual: i + 1, total: filasAGuardar.length } });
    }

    this.setState({ guardando: false, terminado: true, resultados, paso: 4 });
  }

  renderEtiquetaFila(fila) {
    if (fila.filaEstado === "error") return <Tag color="red">Error</Tag>;
    if (fila.filaEstado === "duplicado") return <Tag color="orange">Duplicado</Tag>;
    if (fila.filaEstado === "faltaAsignar") return <Tag color="gold">Falta el insumo</Tag>;
    return <Tag color="green">Lista</Tag>;
  }

  /**
   * Filtra lo que se ve en la tabla del paso 4. No toca `filas`: lo que quedo marcado para
   * guardar se guarda aunque el filtro lo esconda.
   */
  filasFiltradas() {
    const texto = normalizarTexto(this.state.filtroFilas).trim();
    if (!texto) return this.state.filas;

    return this.state.filas.filter((fila) => {
      const campos = [
        fila.nombre,
        fila.proveedor?.stringBuscar,
        fila.datos?.nombreEmisor,
        fila.datos?.rfcEmisor,
        fila.datos?.uuid,
        ...(fila.relacionadas || []).map((r) => `${r.uuid || ""} ${r.codigoFactura || ""}`),
        ...(fila.datos?.conceptos || []).map(
          (valueConcepto) => `${valueConcepto.codigo || ""} ${valueConcepto.descripcion || ""}`
        ),
      ];
      return campos.some((campo) => normalizarTexto(campo).includes(texto));
    });
  }

  /**
   * Al cambiar el filtro se limpia la seleccion: de otro modo "Asignar a seleccionadas" le
   * pegaria a filas que ya no estan a la vista.
   */
  cambiarFiltroFilas(valor) {
    this.setState({ filtroFilas: valor, seleccionFilasKeys: [] });
  }

  /** Descripciones de los conceptos del CFDI, para el tooltip de la columna. */
  renderConceptosFila(fila) {
    const conceptos = fila.datos?.conceptos || [];
    if (conceptos.length === 0) return "-";

    const descripcion = conceptos[0].descripcion || "(sin descripción)";
    const resumen =
      descripcion.length > 60 ? `${descripcion.substring(0, 60)}…` : descripcion;

    return (
      <Tooltip
        title={conceptos.map((valueConcepto, index) => (
          <div key={index}>
            {valueConcepto.cantidad} x {valueConcepto.descripcion || "(sin descripción)"}
          </div>
        ))}
      >
        <span>
          {resumen}
          {conceptos.length > 1 && (
            <Tag color="blue" style={{ marginLeft: 4 }}>
              +{conceptos.length - 1}
            </Tag>
          )}
        </span>
      </Tooltip>
    );
  }

  /** Columna "Aplicar": switch si se puede aplicar, o el motivo por el que no. */
  renderAplicarFila(fila) {
    if (fila.bloqueada) return "-";

    const { aplicacion } = fila;
    if (!aplicacion?.puedeAplicar) {
      if (!(fila.relacionadas || []).length) return <span style={{ color: "#888" }}>Sin facturas</span>;
      return (
        <Tooltip title={aplicacion?.motivo}>
          <Tag>No se aplica</Tag>
        </Tooltip>
      );
    }

    const detalle = aplicacion.montos.map((m) => {
      const relacionada = (fila.relacionadas || []).find((r) => r.facturaId === m.facturaId);
      return `${relacionada?.codigoFactura || relacionada?.uuid}: ${dineroDisabledMask(m.montoPago)}`;
    });

    return (
      <Tooltip title={detalle.map((texto, index) => <div key={index}>{texto}</div>)}>
        <Switch
          size="small"
          checked={!!fila.pagarFactura}
          onChange={(checked) => this.cambiarAplicarFila(fila.uid, checked)}
        />
      </Tooltip>
    );
  }

  renderFacturasRelacionadas(fila) {
    const relacionadas = fila.relacionadas || [];
    if (relacionadas.length === 0) return "-";

    const encontradas = relacionadas.filter((r) => r.encontrada);
    return (
      <Tooltip
        title={relacionadas.map((r, index) => (
          <div key={index}>
            {r.encontrada ? `${r.codigoFactura || r.uuid} (saldo ${dineroDisabledMask(r.saldo)})` : `${r.uuid}: ${r.motivo}`}
          </div>
        ))}
      >
        <span>
          {encontradas.length > 0
            ? encontradas.map((r) => r.codigoFactura || r.uuid).join(", ")
            : <span style={{ color: "#cf1322" }}>No registradas</span>}
          {encontradas.length > 0 && encontradas.length < relacionadas.length && (
            <Tag color="orange" style={{ marginLeft: 4 }}>
              {relacionadas.length - encontradas.length} sin registrar
            </Tag>
          )}
        </span>
      </Tooltip>
    );
  }

  renderPasoNotas() {
    const { filas, guardando, progreso, seleccionFilasKeys, insumoMasivoTemp, filtroFilas } =
      this.state;

    const listas = filas.filter((f) => f.incluir);
    const conProblema = filas.filter((f) => f.filaEstado === "error");
    const sinInsumo = filas.filter((f) => f.filaEstado === "faltaAsignar");
    const aplicadas = listas.filter((f) => f.pagarFactura);
    const filasVisibles = this.filasFiltradas();

    return (
      <>
        <Row gutter={8} style={{ marginBottom: 8 }}>
          <Col><Tag color="green">{listas.length} listas para guardar</Tag></Col>
          {aplicadas.length > 0 && <Col><Tag color="blue">{aplicadas.length} se aplicarán a su factura</Tag></Col>}
          {sinInsumo.length > 0 && <Col><Tag color="gold">{sinInsumo.length} sin insumo asignado</Tag></Col>}
          {conProblema.length > 0 && <Col><Tag color="red">{conProblema.length} con problema</Tag></Col>}
        </Row>

        <Alert
          style={{ marginBottom: 12 }}
          type="info"
          showIcon
          message="Los impuestos y las retenciones son los del XML"
          description={
            "No se leen del insumo. El insumo solo define la familia (si el IVA de la nota va a " +
            "gasto no deducible); el subtotal siempre se abona a Descuentos sobre compras. Si la " +
            "nota relaciona facturas registradas cuyo saldo alcanza, se aplica a ellas al guardarse; " +
            "si no, queda como saldo a favor del proveedor para aplicarla desde Cuentas por Pagar."
          }
        />

        <Row gutter={8} align="middle" style={{ marginBottom: 8 }}>
          <Col xs={24} sm={14}>
            <Input.Search
              allowClear
              placeholder="Buscar por concepto, nota, factura relacionada, proveedor, RFC o UUID"
              value={filtroFilas}
              onChange={(e) => this.cambiarFiltroFilas(e.target.value)}
            />
          </Col>
          {filtroFilas ? (
            <Col>
              <Tag color="blue">
                Mostrando {filasVisibles.length} de {filas.length} notas
              </Tag>
            </Col>
          ) : null}
        </Row>

        <Row gutter={8} align="middle" style={{ marginBottom: 12 }}>
          <Col flex="auto">
            <SelectInsumosItem
              empresaId={this.props.empresaId}
              headersToken={this.props.headersToken}
              tiposInsumos="compra"
              placeholder="Insumo que se pondrá a las notas seleccionadas"
              width="100%"
              dropdownMatchSelectWidth={600}
              value={insumoMasivoTemp?._id}
              insumoPreCargado={insumoMasivoTemp}
              onChange={(value, insumo) => this.setState({ insumoMasivoTemp: insumo })}
            />
          </Col>
          <Col>
            <ButtonItem
              type="primary"
              disabled={seleccionFilasKeys.length === 0 || !insumoMasivoTemp}
              onClick={() => this.asignarInsumoASeleccionadas()}
            >
              Asignar a seleccionadas ({seleccionFilasKeys.length})
            </ButtonItem>
          </Col>
        </Row>

        <Table
          size="small"
          rowKey="uid"
          dataSource={filasVisibles}
          pagination={{ defaultPageSize: 10 }}
          scroll={{ x: 1800 }}
          rowSelection={{
            selectedRowKeys: seleccionFilasKeys,
            onChange: (keys) => this.setState({ seleccionFilasKeys: keys }),
            getCheckboxProps: (r) => ({ disabled: !!r.bloqueada }),
          }}
          columns={[
            {
              title: "Guardar",
              width: 80,
              align: "center",
              render: (_, r) =>
                r.filaEstado === "nuevo" ? (
                  <input type="checkbox" checked={!!r.incluir} onChange={(e) => this.marcarFila(r.uid, e.target.checked)} />
                ) : (
                  <Tooltip title={r.motivoFila}>{this.renderEtiquetaFila(r)}</Tooltip>
                ),
            },
            { title: "Nota", dataIndex: "nombre", width: 130 },
            {
              title: "Proveedor",
              width: 200,
              render: (_, r) => r.proveedor?.stringBuscar || r.datos?.nombreEmisor || "-",
            },
            {
              title: "Conceptos",
              width: 240,
              render: (_, r) => this.renderConceptosFila(r),
            },
            {
              title: "Insumo",
              width: 300,
              render: (_, r) =>
                r.bloqueada ? (
                  <span style={{ color: "#cf1322" }}>{r.motivoFila}</span>
                ) : (
                  <SelectInsumosItem
                    empresaId={this.props.empresaId}
                    headersToken={this.props.headersToken}
                    tiposInsumos="compra"
                    placeholder="Elige el insumo"
                    width="100%"
                    dropdownMatchSelectWidth={600}
                    value={r.insumoId}
                    insumoPreCargado={r.insumoCompleto}
                    onChange={(value, insumo) => this.cambiarInsumoFila(r.uid, value, insumo)}
                  />
                ),
            },
            {
              title: "Facturas relacionadas",
              width: 200,
              render: (_, r) => this.renderFacturasRelacionadas(r),
            },
            {
              title: "Aplicar",
              width: 110,
              align: "center",
              render: (_, r) => this.renderAplicarFila(r),
            },
            { title: "Subtotal", align: "right", render: (_, r) => (r.subtotal !== undefined ? dineroDisabledMask(r.subtotal) : "-") },
            { title: "Impuestos", align: "right", render: (_, r) => (r.totalImpuestos !== undefined ? dineroDisabledMask(r.totalImpuestos) : "-") },
            {
              title: "Retenciones",
              align: "right",
              render: (_, r) => (r.totalRetenciones ? "- " + dineroDisabledMask(r.totalRetenciones) : "-"),
            },
            { title: "Total a pagar", align: "right", render: (_, r) => (r.totalPagar !== undefined ? dineroDisabledMask(r.totalPagar) : "-") },
            {
              title: "Estado",
              render: (_, r) => (
                <>
                  <Tooltip title={r.motivoFila}>{this.renderEtiquetaFila(r)}</Tooltip>
                  {(r.avisos || []).length > 0 && (
                    <Tooltip title={r.avisos.join(" ")}>
                      <WarningOutlined style={{ color: "#faad14", marginLeft: 6 }} />
                    </Tooltip>
                  )}
                </>
              ),
            },
          ]}
          expandable={{
            rowExpandable: (r) =>
              (r.datos?.conceptos || []).length > 0 ||
              (r.arrayImpuestos || []).length > 0 ||
              (r.arrayRetenciones || []).length > 0 ||
              (r.relacionadas || []).length > 0 ||
              (r.avisos || []).length > 0,
            expandedRowRender: (r) => (
              <>
                {(r.avisos || []).length > 0 && (
                  <Alert
                    style={{ marginBottom: 12 }}
                    type="warning"
                    showIcon
                    message="Avisos de este XML"
                    description={r.avisos.map((aviso, index) => (
                      <div key={index}>{aviso}</div>
                    ))}
                  />
                )}

                {(r.relacionadas || []).length > 0 && (
                  <>
                    <b>Facturas relacionadas en el XML</b>
                    <Table
                      size="small"
                      rowKey={(relacionada, index) => index}
                      style={{ marginTop: 4, marginBottom: 12 }}
                      dataSource={r.relacionadas}
                      pagination={false}
                      columns={[
                        { title: "UUID", dataIndex: "uuid" },
                        { title: "Tipo relación", dataIndex: "tipoRelacion", width: 110 },
                        { title: "Factura", width: 140, render: (_, f) => f.codigoFactura || "-" },
                        {
                          title: "Saldo",
                          align: "right",
                          width: 130,
                          render: (_, f) => (f.encontrada ? dineroDisabledMask(f.saldo) : "-"),
                        },
                        {
                          title: "Se aplica",
                          align: "right",
                          width: 130,
                          render: (_, f) => {
                            const monto = r.pagarFactura
                              ? r.aplicacion.montos.find((m) => m.facturaId === f.facturaId)?.montoPago
                              : undefined;
                            return monto ? dineroDisabledMask(monto) : "-";
                          },
                        },
                        {
                          title: "Estado",
                          render: (_, f) =>
                            f.encontrada ? <Tag color="green">Registrada</Tag> : <span style={{ color: "#888" }}>{f.motivo}</span>,
                        },
                      ]}
                    />
                  </>
                )}

                {(r.datos?.conceptos || []).length > 0 && (
                  <>
                    <b>Conceptos del XML</b>
                    <Table
                      size="small"
                      rowKey={(concepto, index) => index}
                      style={{ marginTop: 4, marginBottom: 12 }}
                      dataSource={r.datos.conceptos}
                      pagination={false}
                      columns={[
                        { title: "Código", dataIndex: "codigo", width: 110 },
                        { title: "Descripción", dataIndex: "descripcion" },
                        { title: "Clave SAT", dataIndex: "claveProdServ", width: 100 },
                        { title: "Cantidad", dataIndex: "cantidad", align: "right", width: 90 },
                        {
                          title: "P. Unitario",
                          align: "right",
                          width: 110,
                          render: (_, c) => dineroDisabledMask(c.precioUnitario || 0),
                        },
                        {
                          title: "Importe",
                          align: "right",
                          width: 140,
                          render: (_, c) => (
                            <>
                              {dineroDisabledMask(
                                round2Decimals((c.cantidad || 0) * (c.precioUnitario || 0))
                              )}
                              {c.descuento ? (
                                <Tag color="orange" style={{ marginLeft: 4 }}>
                                  -{c.descuento}%
                                </Tag>
                              ) : null}
                            </>
                          ),
                        },
                        { title: "Impuestos", render: (_, c) => textoTraslados(c.traslados) },
                      ]}
                    />
                  </>
                )}

                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <b>Impuestos del XML</b>
                    {/* La base se muestra porque en un exento o un 0% el importe es 0 y es lo
                        unico que deja ver que si se leyo el renglon, y cuanto va a declarar la DIOT */}
                    {(r.arrayImpuestos || []).map((v) => (
                      <div key={v.impuestoId}>
                        {v.descripcionFactura}: {dineroDisabledMask(v.totalImpuestos)}
                        {typeof v.baseImpuesto === "number" && (
                          <span style={{ color: "#888" }}> (base {dineroDisabledMask(v.baseImpuesto)})</span>
                        )}
                      </div>
                    ))}
                  </Col>
                  <Col xs={24} sm={12}>
                    <b>Retenciones del XML</b>
                    {(r.arrayRetenciones || []).length === 0 && <div>Sin retenciones</div>}
                    {(r.arrayRetenciones || []).map((v) => (
                      <div key={v.retencionId}>
                        {v.descripcionFactura}: {dineroDisabledMask(v.totalRetenciones)}
                        {typeof v.baseRetencion === "number" && (
                          <span style={{ color: "#888" }}> (base {dineroDisabledMask(v.baseRetencion)})</span>
                        )}
                      </div>
                    ))}
                  </Col>
                </Row>
              </>
            ),
          }}
        />

        {guardando && (
          <Progress
            style={{ marginTop: 16 }}
            percent={progreso.total ? Math.round((progreso.actual / progreso.total) * 100) : 0}
            status="active"
          />
        )}

        <Row justify="space-between" style={{ marginTop: 20 }}>
          <ButtonItem onClick={() => this.setState({ paso: 2 })} disabled={guardando}>Atrás</ButtonItem>
          <ButtonItem
            type="primary"
            loading={guardando}
            disabled={listas.length === 0}
            onClick={() => this.guardarTodas()}
          >
            Guardar {listas.length} nota(s)
          </ButtonItem>
        </Row>
      </>
    );
  }

  // ---------- Paso 5: resultado ----------

  renderPasoResultado() {
    const { resultados } = this.state;
    const ok = resultados.filter((r) => r.ok);
    const fallidos = resultados.filter((r) => !r.ok);

    return (
      <>
        <Result
          status={fallidos.length === 0 ? "success" : "warning"}
          title={`${ok.length} nota(s) de crédito registrada(s)`}
          subTitle={fallidos.length > 0 ? `${fallidos.length} no se pudieron guardar` : undefined}
        />
        {fallidos.length > 0 && (
          <Table
            size="small"
            rowKey="uid"
            dataSource={fallidos}
            pagination={false}
            columns={[
              { title: "Nota", dataIndex: "nombre" },
              { title: "Motivo", dataIndex: "motivo" },
            ]}
          />
        )}
        <Row justify="end" style={{ marginTop: 20 }}>
          <ButtonItem type="primary" onClick={() => this.props.history.push("/panel/notasDeCreditoCompra")}>
            Terminar
          </ButtonItem>
        </Row>
      </>
    );
  }

  render() {
    const { paso, loading, terminado, faltaImpuestoExento } = this.state;

    return (
      <>
        <PageHeader
          title="Importar Notas de Crédito XML"
          onBack={() => this.props.history.push("/panel/notasDeCreditoCompra")}
          style={{ border: "1px solid rgb(235, 237, 240)", backgroundColor: "white", marginBottom: 10 }}
        />
        <div className="card-background" style={{ paddingTop: 5 }}>
          <Spin spinning={loading}>
            <Steps current={paso} size="small" style={{ padding: "16px 0 20px" }}>
              <Step title="XMLs" description="Elegir notas" />
              <Step title="Proveedores" description="Dar de alta faltantes" />
              <Step title="Proyecto" description="Proyecto del lote" />
              <Step title="Notas" description="Insumo y aplicación" />
              <Step title="Resultado" icon={terminado ? <CheckCircleOutlined /> : undefined} />
            </Steps>

            {faltaImpuestoExento && (
              <Alert
                style={{ marginBottom: 12 }}
                type="warning"
                showIcon
                message="Esta empresa no tiene dado de alta un IVA Exento"
                description={
                  'Un CFDI con conceptos exentos se va a rechazar, porque un exento no se puede ' +
                  'sustituir por otro IVA. Da de alta en Impuestos un registro con Impuesto = "IVA" ' +
                  'y Tasa o Cuota = "Exento". Ojo: no es lo mismo que el IVA 0%, y la DIOT los ' +
                  'reporta en columnas distintas (Exentos vs Base 0%).'
                }
              />
            )}

            {paso === 0 && this.renderPasoXmls()}
            {paso === 1 && this.renderPasoProveedor()}
            {paso === 2 && this.renderPasoProyecto()}
            {paso === 3 && this.renderPasoNotas()}
            {paso === 4 && this.renderPasoResultado()}
          </Spin>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ImportarNotasDeCreditoXml);
